import { z } from 'zod';

/**
 * End-of-month regulatory packet — Bateman Capital Management ONLY.
 *
 * One PDF per month, `YYYYMM.pdf`, holding the year-to-date Balance Sheet and
 * Profit & Loss, each account's reconciliation proof sheet, and the statements
 * those reconciliations were proved against. BCM has to keep this on file, and
 * a packet assembled from an unreconciled month would attest to balances nobody
 * has checked — so assembly is blocked until every reconcilable account has a
 * completed reconciliation for that month.
 */

export const eomMonthSchema = z
  .string()
  .regex(/^\d{4}-(0[1-9]|1[0-2])$/, 'expected YYYY-MM');

export const eomAssembleInputSchema = z.object({
  /** The month being reported, NOT the month it is being assembled in. */
  month: eomMonthSchema,
});
export type EomAssembleInput = z.infer<typeof eomAssembleInputSchema>;

/** One reconcilable account and whether the selected month is settled. */
export interface EomAccountReadiness {
  bankAccountId: string;
  accountName: string;
  /** GL account code, e.g. "1005". Null if the bank account is unmapped. */
  glCode: string | null;
  glName: string | null;
  reconciled: boolean;
  /**
   * Statement dates of the completed reconciliations covering this month.
   * Plural because an account can be reconciled more than once in a month —
   * BCM's statement dates are not month-ends.
   */
  statementDates: string[];
}

export interface EomReadiness {
  month: string;
  /** First and last calendar day of `month`. */
  periodFrom: string;
  periodTo: string;
  /** Start of the fiscal year containing periodTo — the P&L's opening date. */
  fiscalYearStart: string;
  accounts: EomAccountReadiness[];
  /** True only when every account above is reconciled. Assembly requires it. */
  ready: boolean;
  /** Account names still outstanding, for the message shown when not ready. */
  missing: string[];
  /** Statement PDFs on file for this month, which will go into the packet. */
  statementCount: number;
  /** An existing packet for this month, if one was already assembled. */
  existingDocumentId: string | null;
}

export interface EomAssembleResult {
  month: string;
  fileName: string;
  documentId: string;
  /** Folder the packet was filed under. */
  folder: string;
  /** Date the packet is filed against — the month's last day, not today. */
  documentDate: string;
  sizeBytes: number;
  /** Section names that made it into the PDF, in order. */
  included: string[];
  /** Anything unreadable that was left out, with the reason. */
  skipped: { name: string; reason: string }[];
}

/** Folder in the document vault where monthly packets are filed. */
export const EOM_FOLDER = 'EOM Financials';
